import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * Displays a horizontal tier-pricing table and keeps the tier matching the
 * shopper's current quantity highlighted, live as it changes.
 *
 * Lives in the price block rather than inside `product-form-component` (see
 * the comment in snippets/volume-pricing.liquid for why), so it locates its
 * matching form by `data-product-id` instead of via `closest()`. Products
 * using the bulk-order-form flow have no single `input[name="quantity"]` to
 * read — for those it listens for the `bulk-order-form:quantity-change`
 * event that component dispatches with its own already-summed total instead.
 *
 * @typedef {Object} VolumePricingRefs
 * @property {HTMLElement[]} [volumePricingRows] - The per-tier boxes, each carrying a `data-quantity` threshold.
 *
 * @extends {Component<VolumePricingRefs>}
 */
class VolumePricingComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#attachEventListeners();
    this.#updateActiveTier();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * The product form this table's variant belongs to, matched by product id
   * since this element isn't necessarily a descendant of it.
   * @returns {Element | null}
   */
  #getForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    return document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /**
   * Attaches event listeners for quantity and cart updates
   */
  #attachEventListeners() {
    const { signal } = this.#abortController;

    // Listen on document to catch all events (more reliable than form-only)
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener('bulk-order-form:quantity-change', this.#handleBulkOrderFormUpdate, { signal });
  }

  /**
   * Handles the bulk-order-form's own already-summed quantity total.
   * @param {CustomEvent<{productId?: string, totalQuantity: number}>} event
   */
  #handleBulkOrderFormUpdate = (event) => {
    if (event.detail?.productId !== this.dataset.productId) return;

    this.#updateActiveTier(event.detail.totalQuantity);
  };

  /**
   * Handles quantity selector updates
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to updates for our variant's quantity selector
    const form = this.#getForm();
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#updateActiveTier();
  };

  /**
   * Handles cart updates by refreshing the active tier after the cart response
   * resolves, so data-cart-quantity is up-to-date when we recalculate.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(() => this.#updateActiveTier())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[volume-pricing] Cart update promise rejected:', error);
      });
  };

  /**
   * Gets the total quantity (cart + current input value). Returns null when
   * there's no single quantity input to read (e.g. the bulk-order-form
   * flow), so the caller can leave the server-rendered tier as-is.
   * @returns {number | null}
   */
  #getCurrentQuantity() {
    const form = this.#getForm();
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    if (!quantityInput) return null;

    const cartQty = parseInt(quantityInput.getAttribute('data-cart-quantity') || '0') || 0;
    const inputQty = parseInt(quantityInput.value) || 1;

    return cartQty + inputQty;
  }

  /**
   * Highlights the tier row whose threshold the current quantity qualifies for.
   * Rows are in ascending `data-quantity` order, so the last row the quantity
   * still meets or exceeds is the active one.
   * @param {number} [explicitQuantity] - Use this instead of reading a
   *   single quantity input (the bulk-order-form flow passes its own total).
   */
  #updateActiveTier(explicitQuantity) {
    const rows = this.refs.volumePricingRows;
    if (!rows?.length) return;

    const quantity = explicitQuantity ?? this.#getCurrentQuantity();
    if (quantity === null) return;

    let activeRow = null;

    for (const row of rows) {
      row.classList.remove('volume-pricing__row--active');
      if (quantity >= parseInt(row.dataset.quantity || '0')) {
        activeRow = row;
      }
    }

    activeRow?.classList.add('volume-pricing__row--active');
  }
}

if (!customElements.get('volume-pricing')) {
  customElements.define('volume-pricing', VolumePricingComponent);
}
